import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import axios from 'axios';
import { getFileUrl } from '../config/api';

interface Caregiver {
  _id: string;
  firstName: string;
  lastName: string;
  profilePhoto: string;
  yearsOfExperience: number;
  servicesOffered: string[];
}

interface JobRequest {
  _id: string;
  serviceType: string;
  caregiver?: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePhoto: string;
  };
  assignedCaregiver?: string;
  startDate: string;
  endDate?: string;
  status: 'pending' | 'accepted' | 'declined' | 'in-progress' | 'completed' | 'cancelled' | 'terminated';
  totalCost?: number;
  createdAt: string;
  completedAt?: string;
  hasReview?: boolean;
}

const MyRequests: React.FC = () => {
  usePageTitle('My Requests');
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCaregiver = location.state?.selectedCaregiver as Caregiver | undefined;
  
  const [requests, setRequests] = useState<JobRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [showBookingForm, setShowBookingForm] = useState(!!selectedCaregiver);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Booking form state
  const [bookingData, setBookingData] = useState({
    serviceType: '',
    title: '',
    description: '',
    contractType: 'monthly' as 'weekly' | 'monthly',
    startDate: '',
    endDate: '',
    preferredTime: '09:00-17:00'
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  const serviceTypeNames: Record<string, string> = {
    'nurse-care': 'Nurse Care',
    'everyday-assistance': 'Everyday Assistance'
  };

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    accepted: 'bg-green-100 text-green-700',
    declined: 'bg-red-100 text-red-700',
    'in-progress': 'bg-blue-100 text-blue-700',
    completed: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-700',
    terminated: 'bg-red-100 text-red-700'
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/jobs/customer/my-requests', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRequests(response.data.requests || []);
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to fetch requests');
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const filteredRequests = activeFilter === 'all'
    ? requests
    : requests.filter(req => req.status === activeFilter);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };


  // Booking form submit handler (if needed)
  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookingLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/jobs/book', bookingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowBookingForm(false);
      setSuccessMessage('Booking created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      // Optionally refresh requests
      const response = await axios.get('/api/jobs/customer/my-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans animate-fade-in">
      
      {/* Success Message Banner */}
      {successMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}



      {/* Main Content */}
      <div className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full pb-24 sm:pb-8">
        {/* Header Section */}
        <div className="w-full mb-12 text-center max-w-2xl mx-auto pt-6">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight flex items-center justify-center gap-3">
             <svg className="w-10 h-10 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
             </svg>
             My Requests
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed">All your bookings and service requests in one place.</p>
        </div>

        {/* Floating Action Button (FAB) */}
        <button
          onClick={() => navigate('/customer/find-caregivers')}
          className="fixed bottom-24 right-4 sm:bottom-8 sm:right-8 w-14 h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 z-40 focus:outline-none focus:ring-4 focus:ring-primary-300 active:scale-95"
          title="Book New Request"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>


        {/* Booking Form Modal */}
        {showBookingForm && selectedCaregiver && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Book {selectedCaregiver.firstName} {selectedCaregiver.lastName}</h3>
                <button onClick={() => setShowBookingForm(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Type *</label>
                  <select
                    required
                    value={bookingData.serviceType}
                    onChange={(e) => setBookingData({ ...bookingData, serviceType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select service type</option>
                    {selectedCaregiver.servicesOffered.map(service => (
                      <option key={service} value={service}>{serviceTypeNames[service] || service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={bookingData.title}
                    onChange={(e) => setBookingData({ ...bookingData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Daily care for elderly parent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    required
                    value={bookingData.description}
                    onChange={(e) => setBookingData({ ...bookingData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe your requirements..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type *</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setBookingData({ ...bookingData, contractType: 'weekly' })}
                      className={`px-4 py-3 rounded-lg border-2 font-medium ${bookingData.contractType === 'weekly' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300'}`}
                    >
                      Weekly (৳8,000)
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookingData({ ...bookingData, contractType: 'monthly' })}
                      className={`px-4 py-3 rounded-lg border-2 font-medium ${bookingData.contractType === 'monthly' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300'}`}
                    >
                      Monthly (৳22,000)
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={bookingData.startDate}
                      onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                    <input
                      type="date"
                      required
                      value={bookingData.endDate}
                      onChange={(e) => setBookingData({ ...bookingData, endDate: e.target.value })}
                      min={bookingData.startDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                  <input
                    type="text"
                    required
                    value={bookingData.preferredTime}
                    onChange={(e) => setBookingData({ ...bookingData, preferredTime: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., 09:00-17:00"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowBookingForm(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={bookingLoading}
                    className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {bookingLoading ? 'Sending...' : 'Send Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Filter Dropdown - only show if there are requests */}
        {requests.length > 0 && (
          <div className="mb-6 flex justify-end">
            <label className="mr-2 text-sm text-gray-600 font-medium" htmlFor="request-filter">Filter:</label>
            <select
              id="request-filter"
              value={activeFilter}
              onChange={e => setActiveFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="declined">Declined</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No requests found</h3>
            <p className="text-gray-600 mb-4">
              {activeFilter === 'all' 
                ? "You haven't made any service requests yet."
                : activeFilter === 'pending-review'
                ? "No jobs are currently awaiting your review."
                : `You don't have any ${activeFilter} requests.`}
            </p>
            <button
              onClick={() => navigate('/customer/dashboard')}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Find a Caregiver
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <div
                key={request._id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-gray-100 hover:border-primary-200 group"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {request.caregiver ? (
                    request.caregiver.profilePhoto ? (
                      <img
                        src={getFileUrl(request.caregiver.profilePhoto)}
                        alt={`${request.caregiver.firstName} ${request.caregiver.lastName}`}
                        className="w-12 h-12 rounded-full object-cover border-2 border-primary-200 group-hover:border-primary-400"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center border-2 border-primary-200 group-hover:border-primary-400">
                        <span className="text-lg font-semibold text-primary-600">
                          {request.caregiver.firstName.charAt(0)}{request.caregiver.lastName.charAt(0)}
                        </span>
                      </div>
                    )
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center border-2 border-gray-200">
                      <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-bold text-gray-900 truncate">
                        {serviceTypeNames[request.serviceType] || request.serviceType}
                      </h3>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${statusColors[request.status]}`}
                        title={request.status.charAt(0).toUpperCase() + request.status.slice(1).replace('-', ' ')}>
                        {request.status === 'pending' && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M12 6v6l4 2" strokeWidth="2" /></svg>
                        )}
                        {request.status === 'accepted' && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        )}
                        {request.status === 'in-progress' && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 8v.01" /></svg>
                        )}
                        {request.status === 'completed' && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        )}
                        {request.status === 'declined' && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        )}
                        {request.status === 'cancelled' && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        )}
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1).replace('-', ' ')}
                      </span>
                    </div>
                    {request.caregiver && (
                      <p className="text-gray-600 text-xs mb-1 truncate">
                        with {request.caregiver.firstName} {request.caregiver.lastName}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>
                        {formatDate(request.startDate)}
                        {request.endDate && ` - ${formatDate(request.endDate)}`}
                      </span>
                      {request.totalCost && (
                        <span className="font-bold text-primary-600 ml-2">৳{request.totalCost}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 mt-2 md:mt-0">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/jobs/${request._id}`)}
                      className="px-3 py-1 bg-primary-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity text-xs"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Footer */}
      <footer className="hidden sm:block bg-gray-900 text-white py-8 px-4 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <h4 className="text-2xl font-bold font-proyas tracking-wider mb-2">PROYAS</h4>
          <p className="text-gray-400 text-sm">Quality care, delivered with compassion</p>
          
          {/* Contact Info */}
          <div className="flex justify-center items-center gap-6 mt-4">
            <a href="mailto:contact@proyas.net" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              <span className="text-sm">contact@proyas.net</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61586633764841" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm">Facebook</span>
            </a>
          </div>
          
          <p className="text-gray-500 text-xs mt-4">© 2026 <span className="font-proyas tracking-wider">PROYAS</span>. All rights reserved.</p>
        </div>
      </footer>
      </div>
    );
};

export default MyRequests;
